import { useState } from 'react';
import { Gift, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePricing } from '../contexts/PricingContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { BuyButton } from '../components/shared/BuyButton';

type View = 'choose' | 'redeem';

const GIFT_PRODUCTS = ['questions_q5', 'questions_q10', 'synastry_s1', 'astromatch_a3', 'annual_sub', 'monthly_sub'];

export function GiftCardPage() {
  const [view,        setView]        = useState<View>('choose');
  const [redeemCode,  setRedeemCode]  = useState('');
  const [redeeming,   setRedeeming]   = useState(false);
  const [redeemResult, setRedeemResult] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedProd, setSelectedProd] = useState<string>('questions_q5');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');

  const { getProduct, loading } = usePricing();
  const { format }              = useCurrency();

  async function redeemCard() {
    if (!(redeemCode ?? '').trim()) return;
    setRedeeming(true);
    setRedeemResult(null);

    const { data } = await supabase.functions.invoke('grant-gift-card', {
      body: { code: (redeemCode ?? '').trim().toUpperCase() },
    });

    setRedeeming(false);
    if (data?.data?.granted) {
      setRedeemResult({ success: true, message: `✦ ${data.data.productName} ativado com sucesso!` });
    } else {
      setRedeemResult({ success: false, message: 'Código inválido ou já utilizado.' });
    }
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Vale Presente
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Presenteie com os astros
        </p>
      </div>

      {/* Toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button className={view === 'choose' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setView('choose')}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
          <Gift size={16} /> Presentear
        </button>
        <button className={view === 'redeem' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setView('redeem')}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
          <Mail size={16} /> Resgatar código
        </button>
      </div>

      {/* Purchase flow */}
      {view === 'choose' && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Escolha um produto para presentear alguém especial. O código será enviado por e-mail para o destinatário.
          </p>

          {/* Product selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {!loading && GIFT_PRODUCTS.map(id => {
              const p = getProduct(id);
              if (!p) return null;
              return (
                <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', cursor: 'pointer', backgroundColor: selectedProd === id ? 'rgba(31,78,95,0.06)' : 'var(--bg-surface)', border: `1px solid ${selectedProd === id ? 'var(--border-gold)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-md)' }}>
                  <input type="radio" name="product" value={id} checked={selectedProd === id} onChange={() => setSelectedProd(id)} style={{ accentColor: 'var(--petrol)' }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.15rem', fontSize: 'var(--text-label)' }}>{p.name}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{p.description}</p>
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--petrol)', fontFamily: 'var(--font-display)' }}>{format(p.price_brl)}</span>
                </label>
              );
            })}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="gift-email">E-mail do destinatário</label>
            <input id="gift-email" type="email" className="form-input" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="amigo@email.com" />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="gift-msg">Mensagem pessoal <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></label>
            <textarea id="gift-msg" className="form-textarea" value={personalMessage} onChange={e => setPersonalMessage(e.target.value)} placeholder="Que os astros iluminem sua jornada..." rows={3} maxLength={300} />
          </div>

          <BuyButton
            productId={selectedProd}
            disabled={!recipientEmail.includes('@')}
            label={`✦ Presentear, ${format(getProduct(selectedProd)?.price_brl ?? 0)}`}
          />
        </div>
      )}

      {/* Redeem flow */}
      {view === 'redeem' && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Insira o código recebido para ativar seu presente.
          </p>

          <div className="form-group">
            <label className="form-label" htmlFor="redeem-code">Código do vale presente</label>
            <input id="redeem-code" type="text" className="form-input" value={redeemCode}
              onChange={e => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="ABCD-1234-EFGH"
              style={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'monospace' }}
              maxLength={20} />
          </div>

          {redeemResult && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', backgroundColor: redeemResult.success ? 'rgba(74,222,128,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${redeemResult.success ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 'var(--radius-md)', fontSize: 'var(--text-label)', color: redeemResult.success ? '#4ade80' : '#fca5a5' }}>
              {redeemResult.message}
            </div>
          )}

          <button className="btn btn-primary btn-block" onClick={redeemCard} disabled={redeeming || !(redeemCode ?? '').trim()}>
            {redeeming ? 'Verificando...' : 'Resgatar presente ✦'}
          </button>
        </div>
      )}
    </div>
  );
}
